import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { format, eachDayOfInterval, startOfMonth, endOfMonth, isSameDay, addMonths, subMonths, getDay, isAfter, isBefore } from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface SlotCalendarProps {
    branchId: string;
    seatId: string;
    shift: {
        id: string;
        name: string;
        start_minute: number;
        end_minute: number;
    };
    seatNumber: number;
}

export function SlotCalendar({ branchId, seatId, shift, seatNumber }: SlotCalendarProps) {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const startDate = startOfMonth(currentMonth);
    const endDate = endOfMonth(currentMonth);

    const { data: bookings, isLoading } = trpc.getSeatSchedule.useQuery({
        branchId,
        seatId,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
    });

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Generate empty placeholders for days before the 1st of the month
    const startDayOfWeek = getDay(startDate); // 0 (Sun) - 6 (Sat)
    const placeholders = Array.from({ length: startDayOfWeek });

    const isSlotBooked = (date: Date) => {
        if (!bookings) return false;
        const dateStr = format(date, 'yyyy-MM-dd');

        // Find if any booking overlaps with this slot on this day
        return bookings.some(booking => {
            if (booking.booking_date !== dateStr) return false;

            // Check overlap
            // Booking shift range: booking.start_minute to booking.end_minute
            // Current shift range: shift.start_minute to shift.end_minute

            // Overlap condition: execute shift.start < booking.end && shift.end > booking.start
            return shift.start_minute < booking.end_minute && shift.end_minute > booking.start_minute;
        });
    };

    const getBookingInfo = (date: Date) => {
        if (!bookings) return null;
        const dateStr = format(date, 'yyyy-MM-dd');
        return bookings.find(booking => {
            if (booking.booking_date !== dateStr) return false;
            return shift.start_minute < booking.end_minute && shift.end_minute > booking.start_minute;
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <span className="font-medium text-lg">
                    {format(currentMonth, 'MMMM yyyy')}
                </span>
                <div className="flex gap-1">
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>
            ) : (
                <div className="grid grid-cols-7 gap-1 text-center">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-xs text-muted-foreground font-medium py-1">
                            {day}
                        </div>
                    ))}

                    {placeholders.map((_, i) => (
                        <div key={`placeholder-${i}`} className="aspect-square" />
                    ))}

                    {days.map(day => {
                        const booked = isSlotBooked(day);
                        const booking = getBookingInfo(day);
                        const isToday = isSameDay(day, new Date());

                        return (
                            <div
                                key={day.toISOString()}
                                className={cn(
                                    "aspect-square flex flex-col items-center justify-center rounded-md border text-xs relative",
                                    booked
                                        ? "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/50 dark:text-red-300"
                                        : "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-900/50 dark:text-green-300",
                                    isToday && "ring-2 ring-primary ring-offset-1"
                                )}
                                title={booking ? `Booked by ${booking.user_name}` : "Available"}
                            >
                                <span className="font-semibold">{format(day, 'd')}</span>
                                {booked && (
                                    <span className="text-[10px] opacity-80 truncate w-full px-1">
                                        {booking?.user_name.split(' ')[0]}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="text-xs text-muted-foreground pt-2">
                Showing availability for {shift.name} ({format(shift.start_minute * 60 * 1000, 'HH:mm')}) - {shift.end_minute}) on Seat {seatNumber}.
            </div>
        </div>
    );
}
